import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, X } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { BoardHand } from '../../types/device';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { HAND_COLORS, ROLE_COLORS, ROLE_ICONS, otherHand, roleForeground } from './board-grid/roles';
import {
  circuitFeet,
  circuitHandIndex,
  circuitHandsOf,
  circuitNumbers,
  circuitSteps,
  dragCircuitHold,
  type CircuitHold,
  type CircuitRole,
  type RoleOf,
} from './circuit';

interface CircuitListProps<T extends CircuitHold> {
  /** The circuit as it stands, hands and all — its order *is* the circuit. */
  holds: T[];
  onChange: (holds: T[]) => void;
  /**
   * How this surface reads a hold's role back: a board hold carries one, a circle
   * on a photo carries the colour it is drawn in.
   */
  roleOf: RoleOf<T>;
  /**
   * Where the hold is, in the terms the setter can find it by — a board names its
   * cell. A photo says nothing: a fraction of a picture is not something anybody
   * can look up.
   */
  nameOf?: (hold: T) => string;
  /** Which holds are in hand on the wall, so the two point at the same one. */
  selected?: number[];
  /** Pressing a row picks its hold up on the wall. */
  onSelect?: (index: number) => void;
  /** Whether the circuit says which hand takes each hold. */
  forcedHands?: boolean;
  /**
   * Turns that on and off. The switch sits at the head of the list rather than
   * anywhere else on the surface because the hands *are* this list: they follow
   * from the order it shows, and they are read back on its rows.
   *
   * Left out where the mode is not the surface's to change.
   */
  onForcedHandsChange?: (on: boolean) => void;
  /**
   * Lets the setter flip the hand the circuit's first move is made with, from the
   * badge on that one hold. Left out where the hands are only read back.
   */
  onStartingHandChange?: (hand: BoardHand) => void;
}

/**
 * A circuit in the order it is climbed, draggable into another one.
 *
 * The numbers are worked out from the list's own order rather than stored on a
 * hold, so a reorder renumbers everything by itself. The two ends are not the
 * setter's to move: a climb is climbed from its starts to its top, so a row
 * dropped anywhere settles back inside them (see `circuit.ts`).
 *
 * Feet are not in that order. The climber stands on one for the whole of the run
 * rather than moving to it, so it takes no number and is listed under the run
 * instead of inside it — the same reason its hold wears no number on the wall.
 *
 * One list for every wall a circuit is painted on: the panel over the photo in
 * the member app, the fourth column of the gym panel's scans page, and both of
 * those again for a board's grid. What a hold *is* comes in as `roleOf`, which
 * is the only thing the two walls disagree about.
 */
export default function CircuitList<T extends CircuitHold>({
  holds,
  onChange,
  roleOf,
  nameOf,
  selected = [],
  onSelect,
  forcedHands = false,
  onForcedHandsChange,
  onStartingHandChange,
}: CircuitListProps<T>) {
  const { t } = useTranslation();
  // The same activation constraints as the board's list and the zone manager's:
  // a few pixels of slop, so a tap still reads as a tap on a touch screen.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const steps = circuitSteps(holds, roleOf);
  const feet = circuitFeet(holds, roleOf);
  // The numbers the holds wear, worked out once for the whole run — a match takes
  // two of them, so a row cannot count itself.
  const numbers = circuitNumbers(holds, roleOf);
  // The one hold the setter answers for: which hand the first real move is made
  // with. Everything after it alternates, so that badge is the whole control.
  const handChoice = forcedHands ? circuitHandIndex(holds, roleOf) : null;

  const remove = (index: number) => onChange(holds.filter((_hold, at) => at !== index));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    onChange(dragCircuitHold(holds, roleOf, Number(active.id), Number(over.id)));
  };

  // Whether the hands are forced, at the head of the list. There is nothing to
  // force before there is a run to alternate down, so it comes with the hint
  // rather than standing over an empty panel.
  const handsSwitch = onForcedHandsChange && (
    <div className="flex items-center gap-2">
      <Switch
        id="circuit-forced-hands"
        checked={forcedHands}
        onCheckedChange={onForcedHandsChange}
        aria-label={t('climbs.board.forcedHands.mode')}
      />
      <Label
        htmlFor="circuit-forced-hands"
        className="text-xs text-muted-foreground"
        title={t('climbEditor.circuit.forcedHandsHint')}
      >
        {t('climbs.board.forcedHands.toggle')}
      </Label>
    </div>
  );

  if (holds.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
        {t('climbEditor.circuit.empty')}
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="circuit-hold-list">
      {steps.length > 0 ? (
        <>
          {handsSwitch}
          <p className="text-xs text-muted-foreground">{t('climbEditor.circuit.hint')}</p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            {/* Keyed by where each circle sits in the drawing, which is what
                everything outside — the selection, the numbers — points at. */}
            <SortableContext
              items={steps.map((index) => String(index))}
              strategy={verticalListSortingStrategy}
            >
              <ol className="space-y-1.5">
                {steps.map((index) => (
                  <CircuitStepRow
                    key={index}
                    id={String(index)}
                    hold={holds[index]}
                    role={roleOf(holds[index])}
                    name={nameOf?.(holds[index])}
                    order={numbers[index] ?? ''}
                    selected={selected.includes(index)}
                    onSelect={onSelect ? () => onSelect(index) : undefined}
                    onRemove={() => remove(index)}
                    showHand={forcedHands}
                    onHandChange={
                      index === handChoice && onStartingHandChange
                        ? () => onStartingHandChange(otherHand(holds[index].hand ?? 'left'))
                        : undefined
                    }
                  />
                ))}
              </ol>
            </SortableContext>
          </DndContext>
        </>
      ) : (
        // Feet and nothing else: there are holds on the wall, but no run through
        // them yet.
        <p className="text-sm text-muted-foreground">{t('climbEditor.circuit.empty')}</p>
      )}

      {/* Under the run rather than in it: these are stood on throughout, so
          there is no step of the circuit to put them at. */}
      {feet.length > 0 && (
        <div className="space-y-1.5 border-t pt-2">
          <p className="text-xs text-muted-foreground">{t('climbEditor.circuit.feetHint')}</p>
          <ul className="space-y-1.5">
            {feet.map((index) => (
              <CircuitFootRow
                key={index}
                role={roleOf(holds[index])}
                name={nameOf?.(holds[index])}
                selected={selected.includes(index)}
                onSelect={onSelect ? () => onSelect(index) : undefined}
                onRemove={() => remove(index)}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

interface CircuitStepRowProps {
  hold: CircuitHold;
  role: CircuitRole;
  /** Where the hold is on the wall, where the wall can name it. */
  name?: string;
  /** Where this hold sits in the selection, which is the sortable's id. */
  id: string;
  /** And its place in the run, counted from 1. */
  order: string;
  selected: boolean;
  onSelect?: () => void;
  onRemove: () => void;
  showHand: boolean;
  /** Present on the one hold whose hand the setter chooses; it flips the run. */
  onHandChange?: () => void;
}

function CircuitStepRow({
  hold,
  role,
  name,
  id,
  order,
  selected,
  onSelect,
  onRemove,
  showHand,
  onHandChange,
}: CircuitStepRowProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const swatch = ROLE_COLORS[role];

  return (
    <CircuitRow
      selected={selected}
      innerRef={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      handle={
        <button
          type="button"
          className="cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
          aria-label={t('climbEditor.circuit.reorder', { order })}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" aria-hidden />
        </button>
      }
      badge={
        // The number is the role's colour with whichever of black and white reads
        // on it, and a hairline so a pale badge is not lost in the row.
        <span
          className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full border border-border px-1 text-[10px] font-bold tabular-nums"
          style={{ backgroundColor: swatch, color: roleForeground(swatch) }}
        >
          {order}
        </span>
      }
      role={role}
      name={name}
      // Which hand takes it — two of them on a match, in the order they come onto
      // the hold. Spelled out here because this is where the setter reads the run
      // back: on the wall a hold carries its role, and on a photo the role *is*
      // the colour it is drawn in (see photo-hold-roles), so there is nowhere out
      // there a hand could be shown.
      hand={
        showHand
          ? circuitHandsOf(hold, () => role).map((hand, at) => (
              <HandBadge
                key={hand}
                hand={hand}
                // Only the hand that arrives is the setter's to flip; the other
                // one follows it onto the hold.
                onChange={at === 0 ? onHandChange : undefined}
              />
            ))
          : null
      }
      onSelect={onSelect}
      onRemove={onRemove}
      removeLabel={t('climbEditor.circuit.remove', { order })}
    />
  );
}

/**
 * A foot of the circuit, kept out of the numbered run above it: the climber never
 * moves *to* it, so it has no place in the order and nowhere to be dragged. All
 * it has to do is name the hold and let it go again.
 */
function CircuitFootRow({
  role,
  name,
  selected,
  onSelect,
  onRemove,
}: {
  role: CircuitRole;
  name?: string;
  selected: boolean;
  onSelect?: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  return (
    <CircuitRow
      selected={selected}
      badge={
        <span
          className="size-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: ROLE_COLORS[role] }}
          aria-hidden
        />
      }
      role={role}
      name={name}
      onSelect={onSelect}
      onRemove={onRemove}
      removeLabel={t('climbEditor.circuit.removeFoot')}
    />
  );
}

/** The shape both rows take: handle, badge, what the hold is, the hand, the bin. */
function CircuitRow({
  selected,
  innerRef,
  style,
  handle,
  badge,
  role,
  name,
  hand = null,
  onSelect,
  onRemove,
  removeLabel,
}: {
  selected: boolean;
  innerRef?: (node: HTMLElement | null) => void;
  style?: React.CSSProperties;
  handle?: ReactNode;
  badge: ReactNode;
  role: CircuitRole;
  name?: string;
  hand?: ReactNode;
  onSelect?: () => void;
  onRemove: () => void;
  removeLabel: string;
}) {
  const { t } = useTranslation();
  const Icon = ROLE_ICONS[role];
  const label = (
    <>
      {badge}
      <Icon className="size-4 shrink-0" style={{ color: ROLE_COLORS[role] }} aria-hidden />
      <span className="truncate text-sm">{t(`climbs.holdRoles.${role}.label`)}</span>
      {/* Where it is on the wall, where the wall can say: a board names the cell,
          so a row can be found on the grid without pressing it first. */}
      {name && <span className="shrink-0 text-xs text-muted-foreground">{name}</span>}
    </>
  );

  return (
    <li
      ref={innerRef}
      style={style}
      className={`flex items-center gap-2 rounded-md border bg-background p-1.5 ${
        selected ? 'border-primary' : 'border-border'
      }`}
    >
      {handle}

      {/* The row is the pointer: pressing it picks the hold up on the wall, the
          same thing pressing the hold itself does. Plain text where there is
          nothing to press — a row that looks pressable and is not is worse than
          one that never offered. */}
      {onSelect ? (
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={selected}
          className="flex flex-1 items-center gap-2 overflow-hidden text-left"
        >
          {label}
        </button>
      ) : (
        <span className="flex flex-1 items-center gap-2 overflow-hidden">{label}</span>
      )}

      {hand}

      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className="text-muted-foreground transition-colors hover:text-destructive"
      >
        <X className="size-4" aria-hidden />
      </button>
    </li>
  );
}

/**
 * The hand this hold is taken with — a button on the one hold the setter decides,
 * a label on all the rest, which only alternate from it.
 */
function HandBadge({ hand, onChange }: { hand: BoardHand; onChange?: () => void }) {
  const { t } = useTranslation();
  const label = t(`climbs.board.handsShort.${hand}`);
  const className = 'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase text-white';
  const style = { backgroundColor: HAND_COLORS[hand] };

  if (!onChange) {
    return (
      <span className={className} style={style} data-testid="circuit-hand">
        {label}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onChange}
      aria-label={t('climbs.board.forcedHands.startWith', {
        hand: t(`climbs.board.hands.${hand}`),
      })}
      title={t('climbEditor.circuit.startingHandHint')}
      className={`${className} ring-1 ring-inset ring-white/60`}
      style={style}
      data-testid="circuit-starting-hand"
    >
      {label}
    </button>
  );
}
